/**
 * Session context for managing the current user's session state.
 *
 * Stores sessionId, userName, and userId. All API calls attach the
 * sessionId as an X-Session-Id header via the API client.
 *
 * Session is persisted to sessionStorage so page reloads don't
 * send the user back to the cover page.
 *
 * Access: every session opens view-only. A tab that joined with the session ID
 * can switch to edit mode with the shared edit PIN (see Header's slider). A tab
 * that opened a session from the cover page's past-sessions list never can —
 * it did not know the ID, so it only ever gets to look.
 *
 * canEdit here only gates the UI (disables/hides controls, and the client
 * refuses to even send a write) — it is not what actually enforces view-only.
 * That happens server-side, keyed off `editToken` (see api/main.py's
 * enforce_edit_token and backend/edit_token.py): a request built by hand,
 * skipping this UI entirely, is checked there instead.
 */

import { createContext, useContext, useState, ReactNode } from 'react';
import {
  setClientSessionId, setClientUserName, setClientCanEdit, setClientEditToken,
  getRegisteredEditToken,
} from '@/api/client';
import { recordSession } from '@/utils/recentSessions';
import { resetHandoffOverlap } from '@/utils/handoffOverlap';
import { resetWhatsAppFooter } from '@/utils/whatsappFooter';
import { resetDutyOptions } from '@/utils/dutyOptions';
import { resetOtColorState } from '@/utils/otColors';
import { resetOfficerOrder } from '@/utils/officerOrder';
import { resetHotoNotes } from '@/utils/hotoNotes';
import { clearEditGrant, loadEditGrant, saveEditGrant } from '@/utils/editGrant';

/** How this tab got into the session: by typing/knowing its ID, or by picking
 *  it from the past-sessions list (which never reveals the ID). */
export type SessionAccessMode = 'id' | 'browse';

interface SessionContextType {
  sessionId: string | null;
  userName: string | null;
  userId: number | null;
  accessMode: SessionAccessMode;
  /** Edit mode — off by default; switched on with the edit PIN. */
  canEdit: boolean;
  /** Switch edit mode on (pass the token /sessions/verify-pin just minted) or
   *  off (omit the token). Only ever takes effect for an 'id' session — a
   *  'browse' session stays view-only regardless. */
  setCanEdit: (canEdit: boolean, editToken?: string | null) => void;
  setSession: (
    sessionId: string,
    userName: string,
    userId: number,
    meta?: { forkedFrom?: string; accessMode?: SessionAccessMode },
  ) => void;
  clearSession: () => void;
}

const SessionContext = createContext<SessionContextType | null>(null);

const STORAGE_KEY = 'ac_roster_session';

interface StoredSession {
  sessionId: string | null;
  userName: string | null;
  userId: number | null;
  accessMode: SessionAccessMode;
  /** The proof of edit rights itself — canEdit is derived from whether this
   *  is set, so the two can never drift apart. */
  editToken: string | null;
}

const EMPTY_SESSION: StoredSession = {
  sessionId: null, userName: null, userId: null, accessMode: 'id', editToken: null,
};

function canEditOf(s: StoredSession): boolean {
  return s.accessMode === 'id' && !!s.sessionId && !!s.editToken;
}

function loadFromStorage(): StoredSession {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.sessionId && parsed.userName && parsed.userId != null) {
        const accessMode: SessionAccessMode = parsed.accessMode === 'browse' ? 'browse' : 'id';
        return {
          sessionId: parsed.sessionId,
          userName: parsed.userName,
          userId: parsed.userId,
          accessMode,
          // Edit mode survives a reload of the same tab, never a browse session. A tab that lost its own token
          // (another tab, a browser that reopened the page) takes the one this session's PIN already earned.
          editToken: accessMode !== 'id' ? null
            : (typeof parsed.editToken === 'string' ? parsed.editToken : loadEditGrant(parsed.sessionId)),
        };
      }
    }
  } catch {
    // ignore
  }
  return EMPTY_SESSION;
}

function writeToStorage(session: StoredSession) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // ignore storage errors
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState(() => {
    const stored = loadFromStorage();
    // Sync client module vars immediately on load (before any effects)
    if (stored.sessionId) {
      setClientSessionId(stored.sessionId);
      setClientUserName(stored.userName);
    }
    setClientCanEdit(canEditOf(stored));
    setClientEditToken(stored.editToken);
    return stored;
  });

  const setSession = (
    sid: string,
    name: string,
    uid: number,
    meta?: { forkedFrom?: string; accessMode?: SessionAccessMode },
  ) => {
    const accessMode = meta?.accessMode ?? 'id';
    // A fresh session starts view-only. The one exception is the fork a
    // regeneration lands in: whoever regenerated was already editing, and
    // dropping them to view-only mid-wizard would block its remaining steps.
    // apiClient already minted (and registered) a token for `sid` as part of
    // that same generate/publish call — pick it up here rather than asking
    // the PIN again.
    const wasEditing = canEditOf(session);
    // Otherwise a session this browser already has the PIN's token for (joined again in a new tab, after the
    // browser dropped the old one) is edit from the start.
    const editToken = accessMode === 'id' && !!meta?.forkedFrom && wasEditing
      ? getRegisteredEditToken(sid)
      : accessMode === 'id' ? loadEditGrant(sid) : null;
    if (editToken) saveEditGrant(sid, editToken);
    // Sync client module vars SYNCHRONOUSLY before React re-renders,
    // so that child effects (e.g. useRosterState's refetch) see the
    // correct session ID on their first API call.
    setClientSessionId(sid);
    setClientUserName(name);
    setClientCanEdit(!!editToken);
    setClientEditToken(editToken);
    // Session-scoped settings belong to the session that was just left, not the one
    // being joined — drop them so the next read asks this session.
    resetHandoffOverlap();
    resetWhatsAppFooter();
    resetDutyOptions();
    resetOtColorState();
    resetOfficerOrder();
    resetHotoNotes();
    const next: StoredSession = { sessionId: sid, userName: name, userId: uid, accessMode, editToken };
    setSessionState(next);
    writeToStorage(next);
    // Outlives the tab, unlike the line above — see utils/recentSessions.
    // A session opened from the past-sessions list is not recorded: that list
    // shows IDs, and a browse session's ID is exactly what the user wasn't given.
    if (accessMode === 'id') recordSession(sid, name, meta?.forkedFrom);
  };

  const setCanEdit = (canEdit: boolean, editToken: string | null = null) => {
    setSessionState((prev) => {
      const nextToken = canEdit && prev.accessMode === 'id' && !!prev.sessionId ? editToken : null;
      // Sliding to Edit is remembered for this session; sliding back to View is how it is forgotten.
      if (nextToken) saveEditGrant(prev.sessionId, nextToken);
      else if (!canEdit) clearEditGrant(prev.sessionId);
      setClientCanEdit(!!nextToken);
      setClientEditToken(nextToken);
      const next = { ...prev, editToken: nextToken };
      writeToStorage(next);
      return next;
    });
  };

  const clearSession = () => {
    setClientSessionId(null);
    setClientUserName(null);
    setClientCanEdit(false);
    setClientEditToken(null);
    resetHandoffOverlap();
    setSessionState(EMPTY_SESSION);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  return (
    <SessionContext.Provider
      value={{ ...session, canEdit: canEditOf(session), setCanEdit, setSession, clearSession }}
    >
      {children}
    </SessionContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSession(): SessionContextType {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
